// src/components/iesire/Form.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Row, Col, Form, Button, Table, Alert, InputGroup } from "react-bootstrap";
import { AsyncPaginate } from "react-select-async-paginate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import api from "../../axiosConfig";
import { Badge } from "react-bootstrap";
import GestiunePickerModal from "../pickers/GestiunePickerModal";
import PartPickerModal from "../pickers/PartPickerModal";
import StocCurentPickerModal from "../pickers/StocCurentPickerModal";
import VatPickerModal from "../pickers/VatPickerModal";
import ClientPickerModal from "../pickers/ClientPickerModal";
import TipFacturaPickerModal from "../pickers/TipFacturaPickerModal";
import { toast } from "react-toastify";
import AvizePickerModal from "./AvizePickerModal";
import StornoPickerModal from "./StornoPickerModal";
import SeriiVanzareModal from "./SeriiVanzareModal";
import { format2, format4 } from "../utils/rounding";
import Spinner from "react-bootstrap/Spinner";
import { changeIesireDocument } from "../utils/documentClassification";
import InvoiceCostAllocation from "./InvoiceCostAllocation";
import InvoiceDiscount from "./InvoiceDiscount";
import InvoiceTotals from "./InvoiceTotals";
import CashRegisterButton from "./CashRegisterButton";
import InvoiceStornoButton from "./InvoiceStornoButton";
import InvoicePrintDetails, { paymentDays } from "./InvoicePrintDetails";
/* ===============================
   empty document (CREATE)
================================ */
export const emptyIesire = () => ({
  tip_document: "Factura",
  doc_no: "",
  doc_date: new Date().toISOString().slice(0, 10) + "T00:00:00",
  bxdatascadenta:
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10) +
    "T00:00:00",
  contact_id: 0,
  tip_factura_iesire_id: 1,
  tip_factura_iesire_label: "Normala",
  observatii: "",
  comanda_client: "",
  proiect_client: "",
  bxfinal_bool: false,
  cont_id: null,
  e_tva_inc: false,
  bxsoldint: 0,
  linii: [],
});

// null => Serviciu (fara gestiune / nu scade stoc)
const SERVICE_OPTION = {
  value: null,
  label: "— Serviciu (fara gestiune)",
  raw: null,
};

const emptyLine = () => ({
  uid: makeUid(),

  stock_total: null,
  loadingAvail: false,

  alloc: [], // [{bxstocin_id, qty}]
  stock_err: "",

  id: null,
  bxgestiune_id: null,
  bxpart_id: null,
  descriere: "",
  qty: 0,
  price: 0,
  vat_rate: 0,
  cont_venit_id: null,
  cont_tva_id: null,
  cont_client_id: null,
  bxpart_label: "",
});

const makeUid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `uid_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const toISODateTimeLocal = (s) => {
  // doc.doc_date e "YYYY-MM-DDT00:00:00" la tine -> il lasam ok
  if (!s) return "";
  const d = String(s);
  const ymd = d.slice(0, 10);
  return `${ymd}T00:00:00`;
};

function num(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

/* authFetch poate intoarce Response sau direct JSON */
async function authGetJson(url) {
  const r = await authFetch(url);

  // deja JSON
  if (r && typeof r === "object" && !("ok" in r) && !("json" in r)) return r;

  // Response
  if (r?.ok) return await r.json();

  const msg = r?.text ? await r.text() : "Request failed";
  throw new Error(msg);
}

async function tryManyJson(urls) {
  for (const u of urls) {
    try {
      return await authGetJson(u);
    } catch {
      // try next
    }
  }
  return null;
}

export default function IesireForm({ initial, onSave, onChangePayload, fixedDocumentType = null, busy = false, onCancel, showActions = true }) {
  const navigate = useNavigate();
  const firstPage = useMemo(() => ({ page: 1 }), []);
  const [loadingDoc, setLoadingDoc] = useState(true);

  const [firmaTvaIncasare, setFirmaTvaIncasare] = useState(false);
  const [firmaTvaIncAfiseazaBifa, setFirmaTvaIncAfiseazaBifa] = useState(false);
  const [isPlatitorTva, setIsPlatitorTva] = useState(true);
  const [cuSerii, setCuSerii] = useState(false);

  const [doc, setDoc] = useState(() => ({
    ...emptyIesire(),
    ...(initial || {}),
  }));
  const [err, setErr] = useState("");
  const allocationRef = useRef(null);
  const submitInFlight = useRef(false);

  const [selectedContact, setSelectedContact] = useState(null);
  // const [selectedTipFactura, setSelectedTipFactura] = useState(null);
  const [selectedTipFactura, setselectedTipFactura] = useState(null);

  const setField = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const [gestPicker, setGestPicker] = useState({ show: false, row: null });
  const [partPicker, setPartPicker] = useState({ show: false, row: null });
  const [clientPicker, setClientPicker] = useState(false);

  const [tipFacturaPicker, settipFacturaPicker] = useState(false);
  const [clientCatalogId, setClientCatalogId] = useState(null);
  const [clientPaymentDays,setClientPaymentDays]=useState(30);
  const [initialCatalogId, setInitialCatalogId] = useState(undefined);
  const [showAvizPicker, setShowAvizPicker] = useState(false);
  const [showStornoPicker, setShowStornoPicker] = useState(false);

  // ✅ abia aici ai voie sa folosesti partPicker
  const pickedRow = partPicker?.row; // (am schimbat numele ca sa nu se bata cu row din onPick)
  const rowGestiuneId =
    pickedRow != null ? doc.linii?.[pickedRow]?.bxgestiune_id : null;

  const grupa_produse = (rowGestiuneId ?? doc.bxgestiune_id) != null ? 2 : 3;

  const [vatPicker, setVatPicker] = useState({ show: false, row: null });
  const [seriiPicker, setSeriiPicker] = useState({ show: false, row: null });
  const openVatPicker = (row) => {
    if (isPlatitorTva) {
      setVatPicker({ show: true, row });
    }
  };

  const openGestiunePicker = (rowIndex) =>
    setGestPicker({ show: true, row: rowIndex });
  const openPartPicker = (rowIndex) =>
    setPartPicker({ show: true, row: rowIndex });

  const openTipFacturaPicker = (rowIndex) =>
    settipFacturaPicker({ show: true, row: rowIndex });

  const [triedSubmit, setTriedSubmit] = useState(false);
  const [serii, setSerii] = useState([]);

  useEffect(() => {
    authFetch("/serii")
      .then((data) => {
        const list = data.items || [];
        setSerii(list);
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    setDoc(prev => {
      if (prev.id || prev.doc_serie || prev.doc_no) return prev;
      const matching = serii.filter(s => s.gestiune_id == null && String(s.tip_serie || "").toLowerCase() === (prev.tip_document || "Factura").toLowerCase());
      const defaults = matching.filter(s => s.is_default);
      const selected = defaults.length === 1 ? defaults[0] : matching.length === 1 ? matching[0] : null;
      return selected ? { ...prev, doc_serie: selected.serie, doc_no: String(Math.max(selected.numar_curent || 1, selected.numar_start || 1)) } : prev;
    });
  }, [serii, initial, doc.tip_document, doc.doc_serie, doc.doc_no]);

  useEffect(() => {
    if (onChangePayload) {
      onChangePayload(doc);
    }
  }, [doc, onChangePayload]);

  useEffect(() => {
    // Fetch active company details
    (async () => {
      try {
        const res = await authGetJson("/firma/");
        const items = res?.items || res || [];
        if (items.length > 0) {
          const mainFirma = items[0];
          const hasTva = mainFirma.tva_la_incasare === 1;
          setFirmaTvaIncasare(hasTva);
          setIsPlatitorTva(mainFirma.platitor_tva === 1);
          setCuSerii(mainFirma.cu_serii === 1);

          setDoc((prev) => ({
            ...prev,
            e_tva_inc: prev.id ? prev.e_tva_inc : hasTva,
          }));
        }
      } catch (e) {
        console.error("Failed to load company details:", e);
      }
    })();
  }, []);

  useEffect(() => {
    const d = { ...emptyIesire(), ...(initial || {}) };

    // normalize date
    if (d.doc_date) d.doc_date = String(d.doc_date).slice(0, 10) + "T00:00:00";
    if (d.bxdatascadenta)
      d.bxdatascadenta = String(d.bxdatascadenta).slice(0, 10) + "T00:00:00";

    // Read cont_id from URL query string or localStorage context
    if (!d.id && !d.cont_id) {
      try {
        const params = new URLSearchParams(window.location.search);
        const contIdQuery = params.get("cont_id");
        if (contIdQuery) {
          d.cont_id = Number(contIdQuery);
        } else if (params.get("from_deviz") !== "1" && params.get("from_santier") !== "1") {
          const ctx = JSON.parse(localStorage.getItem("iesire_ctx") || "null");
          if (ctx?.accountId) {
            d.cont_id = Number(ctx.accountId);
          }
        }
      } catch (e) {
        console.error("Error reading context for cont_id:", e);
      }
    }

    // Auto-assign default 4111 journal if missing for Factura
    if (!d.id && !d.cont_id && d.tip_document === "Factura") {
      (async () => {
        try {
          // get accounts that are marked for menus
          const res = await authGetJson("/accounts/menu");
          const items = res?.items || res || [];
          let clientiAcc = items.find(a => String(a.menu_group || "").toUpperCase() === "CLIENTI");
          
          if (!clientiAcc) {
            // fallback to querying all accounts
            const allRes = await authGetJson("/accounts?limit=1000");
            const allItems = allRes?.items || allRes || [];
            clientiAcc = allItems.find(a => String(a.code || a.bxcont_code || "").startsWith("411"));
          }

          if (clientiAcc) {
            setDoc(prev => ({ ...prev, cont_id: clientiAcc.id }));
          }
        } catch(e) {
          console.error("Could not auto-assign 411 account:", e);
        }
      })();
    }

    // Read bxsoldint from URL query string
    if (!d.id && !d.bxsoldint) {
      try {
        const params = new URLSearchParams(window.location.search);
        const bxsoldintQuery = params.get("bxsoldint");
        if (bxsoldintQuery) {
          d.bxsoldint = Number(bxsoldintQuery);
        }
      } catch (e) {
        console.error("Error reading context for bxsoldint:", e);
      }
    }

    // Default e_tva_inc representation based on firmaTvaIncasare
    d.e_tva_inc = d.id ? (d.e_tva_inc === true || Number(d.e_tva_inc) === 1) : firmaTvaIncasare;

    // normalize lines
    d.linii = (d.linii || []).map((ln) => ({
      ...emptyLine(),
      ...ln,
      uid: ln?.uid || makeUid(),
      bxgestiune_id: ln?.bxgestiune_id ?? null,
      bxpart_label: ln?.bxpart_label ?? "",
      stock_total: ln?.stock_total ?? null,
      loadingAvail: false,
    }));

    setDoc(d);

    if (d.contact_id)
      setSelectedContact({
        value: d.contact_id,
        label: `#${d.contact_id}`,
      });
    else setSelectedContact(null);

    if (d.tip_factura_iesire_id)
      setselectedTipFactura({
        value: d.tip_factura_iesire_id,
        label: `#${d.tip_factura_iesire_id}`,
      });
    else setselectedTipFactura(null);
  }, [initial]);

  const [clientAcc, setClientAcc] = useState(null); // {id, code, denumire, bxvaluta_id}
  const requiredDocumentType = !doc.id && fixedDocumentType !== "Factura" && (fixedDocumentType === "Aviz" || String(clientAcc?.code || "").startsWith("418"))
    ? "Aviz" : null;

  useEffect(() => {
    if (!requiredDocumentType) return;
    setDoc(prev => {
      if (prev.tip_document === requiredDocumentType && prev.tip_factura_iesire_id == null) return prev;
      const def = serii.find(s => s.is_default && String(s.tip_serie || "").toLowerCase() === requiredDocumentType.toLowerCase());
      return { ...changeIesireDocument(prev, requiredDocumentType), doc_serie: def?.serie || "", doc_no: def ? String(def.numar_curent) : "" };
    });
  }, [requiredDocumentType, doc.tip_document, doc.tip_factura_iesire_id, serii]);

  useEffect(() => {
    const id = doc.cont_id;
    if (!id) {
      setClientAcc(null);
      return;
    }

    (async () => {
      try {
        const acc = await authFetch(`/accounts/${id}`);
        setClientAcc({
          id: acc.id,
          code: acc.code ?? acc.bxcod ?? acc.bxcont_code ?? "",
          denumire: acc.name ?? acc.denumire ?? "",
          bxvaluta_id: acc.bxvaluta_id ?? acc.valuta_id ?? 1,
        });
      } catch (e) {
        console.error("Error loading client account in Form:", e);
        setClientAcc({ id, code: "", denumire: "", bxvaluta_id: 1 });
      }
    })();
  }, [doc.cont_id]);

  const isForeignCurrency = clientAcc && clientAcc.bxvaluta_id !== 1;

  // Auto-propagate bxvaluta_id from clientAcc to doc
  useEffect(() => {
    if (clientAcc && clientAcc.bxvaluta_id) {
      if (clientAcc.bxvaluta_id !== doc.bxvaluta_id) {
        setDoc((prev) => ({
          ...prev,
          bxvaluta_id: clientAcc.bxvaluta_id,
        }));
      }
    }
  }, [clientAcc, doc.bxvaluta_id]);

  // Fetch BNR exchange rate automatically for foreign currency
  useEffect(() => {
    const fetchBnrRates = async () => {
      const valutaId = doc.bxvaluta_id;
      if (!valutaId) return;

      if (parseInt(valutaId) === 1) {
        if (doc.bxvaluta_curs !== "1.0000" && doc.bxvaluta_curs !== 1.0) {
          setDoc((prev) => ({
            ...prev,
            bxvaluta_curs: 1.0,
            bxcurs_invoice: null,
          }));
        }
        return;
      }

      // Check if this is a newly created document or has default 1.0 rate
      const isNew = !doc.id || Number(doc.bxvaluta_curs) === 1.0 || !doc.bxvaluta_curs;

      let newBnrCurs = doc.bxvaluta_curs;
      let newInvoiceCurs = doc.bxcurs_invoice;

      if (doc.doc_date) {
        try {
          const dateStr = String(doc.doc_date).substring(0, 10);
          const res = await api.get(`/bxvaluta/curs/fetch`, {
            params: { valuta_id: valutaId, date: dateStr }
          });
          if (res.data && res.data.rate !== undefined) {
            const rateVal = parseFloat(res.data.rate).toFixed(4);
            if (isNew) {
              newBnrCurs = rateVal;
              newInvoiceCurs = rateVal;
            }
          }
        } catch (err) {
          console.error("Eroare la preluare curs BNR:", err);
        }
      }

      if (doc.bxvaluta_curs !== newBnrCurs || doc.bxcurs_invoice !== newInvoiceCurs) {
        setDoc((prev) => ({
          ...prev,
          bxvaluta_curs: newBnrCurs,
          bxcurs_invoice: newInvoiceCurs,
        }));
      }
    };

    fetchBnrRates();
  }, [doc.bxvaluta_id, doc.doc_date, doc.id]);

  useEffect(() => {
    if (doc.contact_id) {
      authGetJson(`/contacte/${doc.contact_id}`)
        .then((data) => {
          const days=data?.termen_plata_zile??30;
          setClientPaymentDays(days);
          if(!doc.id)setDoc(prev=>{if(prev.contact_id!==doc.contact_id||!prev.doc_date)return prev;const due=new Date(prev.doc_date);due.setDate(due.getDate()+days);return {...prev,bxdatascadenta:due.toLocaleDateString('sv-SE')+'T00:00:00'};});
          const catId = data?.bxcatalog_id ?? null;
          setClientCatalogId(catId);
          setInitialCatalogId((prev) => (prev === undefined ? catId : prev));
          
          if (!doc.contact_label && data?.bxdenumire) {
            setDoc((prev) => ({ ...prev, contact_label: data.bxdenumire }));
          }
        })
        .catch((err) => {
          console.error("Failed to load contact catalog:", err);
          setClientCatalogId(null);
          setInitialCatalogId((prev) => (prev === undefined ? null : prev));
        });
    } else {
      setClientCatalogId(null);
      setInitialCatalogId((prev) => (prev === undefined ? null : prev));
    }
  }, [doc.contact_id]);

  useEffect(() => {
    if (initial?.san_situatie_id || initial?.contor_facturare) return;
    // Only update existing lines if catalog changes from the initial loaded catalog (i.e. user selection/interaction)
    if (
      clientCatalogId &&
      initialCatalogId !== undefined &&
      clientCatalogId !== initialCatalogId
    ) {
      (doc.linii || []).forEach((ln, idx) => {
        if (ln.bxpart_id) {
          authGetJson(
            `/pret-catalog/lookup?catalog_id=${clientCatalogId}&bxpart_id=${ln.bxpart_id}`,
          )
            .then((priceData) => {
              if (priceData && priceData.pret != null) {
                updateLine(idx, { price: Number(priceData.pret).toFixed(4) });
              }
            })
            .catch(() => {
              // keep current price if not in new catalog
            });
        }
      });
    }
  }, [clientCatalogId, initialCatalogId, initial?.san_situatie_id]);

  useEffect(() => {
    // Auto-populate bxpart_id for Deviz line if it's missing
    const hasDevizLine = doc.linii && doc.linii.length === 1 && !doc.linii[0].bxpart_id && doc.linii[0].descriere === "Reparatie conform deviz";
    if (hasDevizLine) {
      (async () => {
        try {
          let res = await authGetJson("/bxpart/?tip_grupa_produse=3&limit=1");
          let items = res?.items || res || [];
          if (!items.length) {
            res = await authGetJson("/bxpart/?tip_grupa_produse=1&limit=1");
            items = res?.items || res || [];
          }
          if (items.length > 0) {
            setDoc((prev) => {
              if (prev.linii && prev.linii.length > 0 && !prev.linii[0].bxpart_id) {
                const no = [...prev.linii];
                no[0] = {
                  ...no[0],
                  bxpart_id: items[0].id,
                  bxpart_label: items[0].bxname || items[0].bxdenumire || items[0].bxcode,
                };
                return { ...prev, linii: no };
              }
              return prev;
            });
          }
        } catch (e) {
          console.error("Failed to fetch default service for deviz line", e);
        }
      })();
    }
  }, [doc.linii]);

  const [vatOptions, setVatOptions] = useState([]);
  const [vatLoading, setVatLoading] = useState(false);
  // useEffect(() => {
  //   let alive = true;
  //   (async () => {
  //     setVatLoading(true);
  //     try {
  //       const rows = await authFetch("/bxvat/");
  //       const items = (rows.items ?? rows) || [];
  //       const opts = items
  //         .slice()
  //         .sort((a, b) => Number(a.ordine ?? 0) - Number(b.ordine ?? 0))
  //         .map((v) => ({
  //           value: v.id,
  //           procent: Number(v.procent ?? 0),
  //           label: `${Number(v.procent ?? 0).toFixed(2)}% — ${v.cod}${v.descriere ? " " + v.descriere : ""}`,
  //         }));
  //       if (alive) setVatOptions(opts);
  //     } catch (e) {
  //       console.error("load bxvat failed:", e);
  //     } finally {
  //       if (alive) setVatLoading(false);
  //     }
  //   })();
  //   return () => {
  //     alive = false;
  //   };
  // }, []);

  const locked = !!doc?.bxfinal_bool;
  const statementInvoice = !!doc.deviz_id || !!initial?.san_situatie_id || !!initial?.contor_facturare;

  const handleVatChange = (lineIndex, vatIdStr) => {
    const vatId = vatIdStr ? Number(vatIdStr) : null;
    const opt = vatOptions.find((o) => o.value === vatId);

    setDoc((prev) => ({
      ...prev,
      linii: (prev.linii || []).map((ln, i) => {
        if (i !== lineIndex) return ln;
        return {
          ...ln,
          vat_id: vatId,
          vat_rate: opt?.procent ?? 0,
        };
      }),
    }));
  };

  const num = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };

  const calcFaraTVA = (ln) => num(ln.qty) * num(ln.price);
  const calcTVA = (ln) =>
    isPlatitorTva ? calcFaraTVA(ln) * (num(ln.vat_rate) / 100) : 0;

  const onQtyChange = (i, value) => {
    const q = Number(value || 0);
    const ln = doc.linii[i];

    const tip = Number(ln.tip_grupa_produse ?? 2);
    const isStock = tip === 2;

    // ✅ Serviciu (1 sau 3): qty liber, fara stoc_total
    if (!isStock) {
      updateLine(i, { qty: q, stock_err: "" });
      return;
    }

    // ✅ Stoc (2): trebuie sa ai disponibil incarcat
    if (ln.stock_total == null) {
      updateLine(i, {
        stock_err: "Nu s-a incarcat disponibilul (alege Part din nou)",
      });
      return;
    }

    if (q > Number(ln.stock_total)) {
      updateLine(i, {
        qty: Number(ln.stock_total),
        stock_err: `Stoc insuficient. Disponibil: ${ln.stock_total}`,
      });
      return;
    }

    updateLine(i, { qty: q, stock_err: "" });
  };

  /* ===============================
     CONTACT DROPDOWN (ca intrare)
  ================================ */
  const loadContactOptions = async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();

    const data = await tryManyJson([
      `/contacte/?skip=${(page - 1) * 10}&limit=10&client=${encodeURIComponent(q)}`,
      `/contacte/?skip=${(page - 1) * 10}&limit=10&q=${encodeURIComponent(q)}`,
    ]);

    const items = data?.items || data || [];
    const total = data?.total ?? null;

    const options = items
      .map((it) => ({
        value: it.id ?? it.bxid,
        label: it.bxdenumire ?? it.name ?? `#${it.id ?? it.bxid}`,
        raw: it,
      }))
      .filter((o) => o.value);

    const hasMore = total != null ? page * 10 < total : options.length === 10;
    return { options, hasMore, additional: { page: page + 1 } };
  };

  const handleContactChange = (opt) => {
    setSelectedContact(opt);
    setField("contact_id", opt?.value || 0);
  };

  /* ===============================
     TIP FACTURA IESIRE DROPDOWN
  ================================ */
  const loadTipFacturaOptions = async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();

    const data = await tryManyJson([
      `/tip_factura_iesire/?skip=${(page - 1) * 10}&limit=10&q=${encodeURIComponent(q)}`,
      `/tipfacturaiesire/?skip=${(page - 1) * 10}&limit=10&q=${encodeURIComponent(q)}`,
    ]);

    const items = data?.items || data || [];
    const total = data?.total ?? null;

    const options = items
      .map((it) => ({
        value: it.id ?? it.tip_factura_iesire_id ?? it.bxid,
        label:
          it.denumire ??
          it.name ??
          it.bxdenumire ??
          `#${it.id ?? it.tip_factura_iesire_id ?? it.bxid}`,
        raw: it,
      }))
      .filter((o) => o.value);

    const hasMore = total != null ? page * 10 < total : options.length === 10;
    return { options, hasMore, additional: { page: page + 1 } };
  };

  const handleTipFacturaChange = (opt) => {
    settipFacturaPicker(false);
    setField("tip_factura_iesire_id", opt?.id ?? null);
    setField("tip_factura_iesire_label", opt?.label ?? "");
  };

  /* ===============================
     GESTIUNE (pe linie) DROPDOWN
  ================================ */
  const loadGestiuneOptions = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      if (q.length === 0) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const data = await tryManyJson([
        `/bxgestiune/?skip=${(page - 1) * 15}&limit=10&q=${encodeURIComponent(q)}`,
      ]);

      const items = data?.items || data || [];
      const total = data?.total ?? null;

      const pageOptions = items
        .map((it) => ({
          value: it.id ?? it.bxid,
          label:
            it.bxdenumire ?? it.denumire ?? it.name ?? `#${it.id ?? it.bxid}`,
          raw: it,
        }))
        .filter((o) => o.value);

      const options =
        page === 1 ? [SERVICE_OPTION, ...pageOptions] : pageOptions;
      const hasMore =
        total != null ? page * 10 < total : pageOptions.length === 10;
      return { options, hasMore, additional: { page: page + 1 } };
    },
    [firstPage],
  ); // firstPage nu e folosit aici, dar pastreaza deps minimale

  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };

  const fetchLotsForPart = async (gestiuneId, partId) => {
    if (!gestiuneId || !partId) return { total: 0 };
    const total = await authFetch(
      `/stock/stocin/${partId}/total?gestiune_id=${gestiuneId}`,
    );
    return { total: Number(total || 0) };
  };

  /* ===============================
     PART DROPDOWN (pe linie)
  ================================ */
  const loadPartOptions = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();

      // console.log("loadPartOptions inputValue=", JSON.stringify(inputValue));
      // // optional: nu cauta pe gol
      // if (!q) {
      //   return { options: [], hasMore: false, additional: { page: 1 } };
      // }
      // ✅ BLOCARE TOTALA pentru q gol
      if (q.length === 0) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const data = await tryManyJson([
        `/bxpart/?skip=${(page - 1) * 10}&limit=10&q=${encodeURIComponent(q)}`,
      ]);

      const items = data?.items || data || [];
      const total = data?.total ?? null;

      const options = items
        .map((it) => {
          const id = it.id ?? it.bxid;
          const code = it.cod ?? it.bxcod ?? it.sku ?? "";
          const name =
            it.denumire ?? it.bxname ?? it.name ?? it.descriere ?? "";

          return {
            value: id,
            label: `${code ? code + " • " : ""}${name || "#" + id}`,
            raw: it,
          };
        })
        .filter((o) => o.value);

      const hasMore = total != null ? page * 10 < total : options.length === 10;

      return { options, hasMore, additional: { page: page + 1 } };
    },
    [],
  );

  /* ===============================
     LINES CRUD
  ================================ */
  const addLine = () =>
    setDoc((d) => ({ ...d, linii: [...(d.linii || []), emptyLine()] }));
  const delLine = (idx) =>
    setDoc((d) => ({
      ...d,
      linii: (d.linii || []).filter((_, i) => i !== idx),
    }));

  const updateLine = (idx, patch) => {
    setDoc((d) => {
      const l = [...(d.linii || [])];
      l[idx] = { ...l[idx], ...patch };
      return { ...d, linii: l };
    });
  };

  const handlePickStorno = (item, qtyToStorno) => {
    const vatPercent = item.vat_rate ?? 21;
    const qty = -qtyToStorno;
    const price = item.price;
    const base = +(qty * price).toFixed(2);
    const vat = +((base * vatPercent) / 100).toFixed(2);
    const total = +(base + vat).toFixed(2);

    const stornoLine = {
      storno_source_line_id: item.iesire_ln_id,
      uid: makeUid(),
      bxpart_id: item.bxpart_id,
      descriere: `Storno la fact. nr. ${item.doc_no} (Lot: ${item.nr_lot || "standard"})`,
      bxgestiune_id: item.bxgestiune_id,
      qty: qty.toFixed(4),
      price: price.toFixed(4),
      vat_rate: vatPercent,
      vat_id: null,
      vat_label: `TVA${vatPercent}`,
      suma_fara_tva: base,
      suma_tva: vat,
      suma_total: total,
      cont_venit_id: item.cont_venit_id || null,
      cont_tva_id: item.cont_tva_id || null,
      cont_client_id: item.cont_client_id || null,
      bxpart_label: `${item.part_code} • ${item.part_name}`,
      bxgestiune_label: `${item.gestiune_code} • ${item.gestiune_name}`,
      bxstocin_id: item.bxstocin_id,
      stock_total: 0,
      loadingAvail: false,
      alloc: [],
      stock_err: "",
    };

    setDoc((prev) => ({
      ...prev,
      linii: [...(prev.linii || []), stornoLine],
    }));

    toast.success(
      `S-a adaugat pozitia de storno pentru ${item.part_name} (${qty.toFixed(3)}).`,
    );
  };

  const handlePickAviz = async (avizId) => {
    try {
      const aviz = await authFetch(`/iesire/${avizId}`);
      if (!aviz || !aviz.linii) return;

      const importedLines = aviz.linii.map((ln) => {
        const qty = Number(ln.qty) || 0;
        const price = Number(ln.price) || 0;
        const base = +(qty * price).toFixed(2);
        const vatPercent = 21; // Standard Romanian sales VAT is 21%
        const vat = +((base * vatPercent) / 100).toFixed(2);
        const total = +(base + vat).toFixed(2);

        return {
          uid: makeUid(),
          bxpart_id: ln.bxpart_id,
          descriere: ln.descriere || "",
          bxgestiune_id: ln.bxgestiune_id,
          qty: qty,
          price: price,
          vat_rate: vatPercent,
          vat_id: 5, // 'TVA21'
          vat_label: "TVA21",
          suma_fara_tva: base,
          suma_tva: vat,
          suma_total: total,
          cont_venit_id: ln.cont_venit_id || null,
          cont_tva_id: ln.cont_tva_id || null,
          cont_client_id: ln.cont_client_id || null,
          bxpart_label: ln.bxpart_label || "",
          bxgestiune_label: ln.bxgestiune_label || "",
          stock_total: 0,
          loadingAvail: false,
          alloc: [],
          stock_err: "",
        };
      });

      const isCurrentEmpty =
        !doc.linii ||
        doc.linii.length === 0 ||
        (doc.linii.length === 1 && !doc.linii[0].bxpart_id);

      const newLinii = isCurrentEmpty
        ? importedLines
        : [...doc.linii, ...importedLines];

      setDoc((prev) => ({
        ...prev,
        linii: newLinii,
      }));

      // Fetch availability for stock items
      importedLines.forEach((ln) => {
        if (ln.bxgestiune_id && ln.bxpart_id) {
          const key = makeAvailKey(
            ln.bxgestiune_id,
            doc.doc_date,
            ln.bxpart_id,
          );
          lastAvailKeyRef.current.set(ln.uid, key);
          fetchAvailForLine(ln.uid, ln.bxgestiune_id, ln.bxpart_id);
        }
      });

      toast.success(
        `S-au preluat ${importedLines.length} repere din avizul selectat.`,
      );
    } catch (e) {
      toast.error(`Eroare la preluarea avizului: ${e.message || e}`);
    }
  };

  const lastAvailKeyRef = React.useRef(new Map()); // uid -> key
  const reqSeqRef = React.useRef(new Map()); // uid -> seq

  const makeAvailKey = (gest, docDate, partId) =>
    `${gest ?? ""}|${String(docDate || "").slice(0, 10)}|${partId ?? ""}`;

  const fetchAvailForLine = async (uid, gestiuneId, partId) => {
    if (!gestiuneId || !partId) return;

    const prev = reqSeqRef.current.get(uid) ?? 0;
    const seq = prev + 1;
    reqSeqRef.current.set(uid, seq);

    // set loading
    setDoc((d) => ({
      ...d,
      linii: (d.linii || []).map((x) =>
        x.uid === uid ? { ...x, loadingAvail: true } : x,
      ),
    }));

    try {
      const as_of = toISODateTimeLocal(doc.doc_date);
      const url = `/stock/available?bxpart_id=${partId}&gestiune_id=${gestiuneId}&as_of=${encodeURIComponent(as_of)}`;
      const r = await authFetch(url);
      console.log("7777777777777777777777777777777777777");

      let qty;
      if (r && typeof r === "object" && !("ok" in r) && !("json" in r))
        qty = r; // JSON direct
      else if (r?.ok) qty = await r.json();
      else qty = r;
      console.log("8888888888888888888888");
      console.log(qty);
      const val =
        typeof qty === "number" ? qty : (qty?.qty ?? qty?.available ?? 0);

      // ignore stale
      if (reqSeqRef.current.get(uid) !== seq) return;

      setDoc((d) => ({
        ...d,
        linii: (d.linii || []).map((x) =>
          x.uid === uid
            ? { ...x, stock_total: Number(val || 0), loadingAvail: false }
            : x,
        ),
      }));
    } catch (e) {
      if (reqSeqRef.current.get(uid) !== seq) return;
      setDoc((d) => ({
        ...d,
        linii: (d.linii || []).map((x) =>
          x.uid === uid ? { ...x, loadingAvail: false } : x,
        ),
      }));
    }
  };

  /* ===============================
     VALIDARE + SAVE
  ================================ */
  const validate = () => {
    if (!doc.contact_id || Number(doc.contact_id) <= 0)
      return "Alege clientul.";

    for (let i = 0; i < (doc.linii || []).length; i++) {
      const ln = doc.linii[i];
      if (ln.storno_source_line_id) {
        if (num(ln.qty) === 0) return `Linia ${i+1}: cantitatea storno nu poate fi zero.`;
        continue;
      }
      const isStorno = ln.bxstocin_id != null;

      if (isStorno) {
        if (num(ln.qty) >= 0) {
          return `Linia ${i + 1} (Storno): Cantitatea trebuie sa fie negativa (mai mica decat 0).`;
        }
      } else {
        const label = ln.bxpart_label || "";
        const isDiscount = label.startsWith("DISCOUNT •") || label === "DISCOUNT" || (ln.bxpart && ln.bxpart.cod === "DISCOUNT");
        if (isDiscount) {
          if (num(ln.qty) === 0) {
            return `Linia ${i + 1}: qty nu poate fi 0`;
          }
        } else {
          if (num(ln.qty) <= 0) {
            return `Linia ${i + 1}: qty trebuie > 0`;
          }
        }
      }

      const isService = ln.bxgestiune_id == null;
      if (!isService) {
        if (!ln.bxpart_id)
          return `Linia ${i + 1}: alege Part (obligatoriu la marfa).`;
      }

      if (!isService) {
        if (!ln.bxpart_id)
          return `Linia ${i + 1}: alege Part (obligatoriu la marfa).`;
        // if (!ln.stock_lots || ln.stock_lots.length === 0)
        //   return `Linia ${i + 1}: nu s-a incarcat stocul (alege Part din nou).`;
        if (ln.stock_err) return `Linia ${i + 1}: stoc insuficient.`;
      }
    }
    return "";
  };

  // async function onSave(payload) {
  //   try {
  //     const saved = await authFetch(`/iesire`, {
  //       method: payload.id ? "PUT" : "POST",
  //       body: JSON.stringify(payload),
  //     });
  //     return saved; // DOAR pe succes
  //   } catch (e) {
  //     // NU: setDoc(...) / reset aici
  //     throw e; // lasa handleSubmit sa afiseze eroarea
  //   }
  // }

  const submitForm = useCallback(async () => {
    setErr("");
    setTriedSubmit(true);

    const msg = validate();
    if (msg) {
      setErr(msg);
      return;
    }

    if (!doc.cont_id) {
      setErr("Eroare: Nu este selectat niciun cont (Jurnal) pentru acest document! (ex: 4111)");
      toast.error("Documentul nu are un Jurnal (cont_id) valid.");
      return;
    }

    if (locked)
      return setErr("Documentul este finalizat si nu mai poate fi modificat.");



    const d = String(doc.doc_date || "").slice(0, 10);
    const ds = doc.bxdatascadenta
      ? String(doc.bxdatascadenta).slice(0, 10)
      : "";
    const payload = {
      ...doc,
      doc_date: d ? d + "T00:00:00" : doc.doc_date,
      bxdatascadenta: ds ? ds + "T00:00:00" : null,
      e_tva_inc: doc.e_tva_inc ? 1 : 0,
    };

    if (doc.linii && doc.linii.length > 0) {
      for (let i = 0; i < doc.linii.length; i++) {
        const ln = doc.linii[i];
        if (!ln.bxpart_id) {
          toast.error(`Eroare: Linia ${i + 1} ("${ln.descriere}") nu are un articol / serviciu valid selectat! Trebuie sa alegi un produs sau serviciu in coloana Part.`);
          setErr(`Linia ${i + 1} nu are un articol selectat. Ai incercat sa faci factura din deviz si nu s-a gasit automat un serviciu in nomenclator. Selecteaza manual!`);
          return;
        }
      }
    }

    if (submitInFlight.current) return;
    submitInFlight.current = true;
    try {
      payload.cost_allocation = await allocationRef.current.prepare(payload);
      if (onSave) {
        const saved = await onSave(payload);
        if (saved) setDoc(saved);
      }
    } catch (e) {
      // IMPORTANT: nu atinge doc aici -> nu pierzi nimic
      setErr(e?.detail || e?.message || "Eroare la salvare.");
    } finally {
      submitInFlight.current = false;
    }
  }, [doc, locked, onSave]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    await submitForm();
  };

  useEffect(() => {
    const handleGlobalSubmit = async () => {
      await submitForm();
    };
    window.addEventListener("iesire-submit", handleGlobalSubmit);
    return () => {
      window.removeEventListener("iesire-submit", handleGlobalSubmit);
    };
  }, [submitForm]);

  // const openPdf = (id) => {
  //   window.open(`http://localhost:8000/iesire/${id}/pdf`, "_blank");
  // };

  async function openPdf(id) {
    const blob = await authFetch(`/iesire/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
  // function openPartPicker(i) {
  //   const isServiciu = Number(doc?.gestiune?.is_serviciu || 0) === 1;
  //   const filter = isServiciu ? { tip_grupa_produse: 1 } : {};

  //   setPartPicker({
  //     show: true,
  //     rowIndex: i,
  //     filter,
  //   });
  // }

  // async function openPdf(id) {
  //   const blob = await authFetch(`/iesire/${id}/pdf`, { responseType: "blob" });

  //   const url = URL.createObjectURL(blob);
  //   window.open(url, "_blank");
  //   setTimeout(() => URL.revokeObjectURL(url), 60_000);
  // }

  /* ===============================
     RENDER
  ================================ */
  return (
    <Form onSubmit={handleSubmit} autoComplete="off">
      {err && <Alert variant="danger">{err}</Alert>}
      {!!doc.storno_sources?.length && <Alert variant="info">Storno pentru {doc.storno_sources.map(item=><a className="me-2" key={item.id} href={`/iesire/${item.id}`}>factura {item.doc_no || item.id}</a>)}. Puteți șterge pozițiile pe care nu le stornați.</Alert>}
      {!!doc.storno_settlement && <Alert variant="success">Compensare automată cu factura originală: {doc.storno_settlement.amount} — document {doc.storno_settlement.doc_no}, cu notă contabilă.</Alert>}
      {!!doc.storno_documents?.length && <Alert variant="info">Facturi storno: {doc.storno_documents.map(item=><a className="me-2" key={item.id} href={`/iesire/${item.id}`}>{item.doc_no || item.id}</a>)}</Alert>}

      {/* TOP */}
      <Row className="mb-3">
        <Col md={2}>
          <Form.Group>
            <Form.Label>Tip Document</Form.Label>
            <Form.Select
              value={doc.tip_document === "Bon Fiscal" ? "Bon fiscal" : (doc.tip_document || "Factura")}
              onChange={(e) => {
                if (requiredDocumentType) return;
                const tip = e.target.value;
                setDoc(prev => changeIesireDocument(prev, tip));
                  if (!doc.id) {
                  const defSerie = serii.find(s => s.tip_serie.toLowerCase() === tip.toLowerCase() && s.is_default);
                  if (defSerie) {
                    setDoc(prev => ({...prev, tip_document: tip, doc_serie: defSerie.serie, doc_no: defSerie.numar_curent.toString()}));
                  } else {
                    setField("doc_serie", "");
                    setField("doc_no", "");
                  }
                }
              }}
              disabled={locked || !!requiredDocumentType}
            >
              <option value="Factura">Factura</option>
              <option value="Proforma">Proforma</option>
              <option value="Aviz">Aviz</option>
              <option value="Bon fiscal">Bon fiscal</option>
            </Form.Select>
            {requiredDocumentType && <Form.Text>Tipul documentului este stabilit de meniul Avize clienți.</Form.Text>}
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Nr document (Serie + Numar)</Form.Label>
            <InputGroup>
              <Form.Select
                value={doc.doc_serie || ""}
                onChange={(e) => {
                  const serie = e.target.value;
                  setField("doc_serie", serie);
                  const found = serii.find(s => s.serie === serie);
                  if (found && !doc.id) {
                    setField("doc_no", found.numar_curent.toString());
                  }
                }}
                disabled={locked}
                style={{ maxWidth: "120px" }}
              >
                <option value="">Serie</option>
                {serii.filter(s => s.tip_serie.toLowerCase() === (doc.tip_document || "Factura").toLowerCase()).map(s => (
                  <option key={s.id} value={s.serie}>{s.serie}</option>
                ))}
              </Form.Select>
              <Form.Control
                disabled={locked}
                value={doc.doc_no || ""}
                readOnly={!doc.id && !!doc.deviz_id}
                placeholder={!doc.id && doc.deviz_id ? "Automat la salvare" : ""}
                onChange={(e) => setField("doc_no", e.target.value)}
                autoComplete="nope"
              />
            </InputGroup>
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group>
            <Form.Label>Data</Form.Label>
            <DatePicker
              selected={doc.doc_date ? new Date(doc.doc_date) : null}
              onChange={(date) => {
                if (!date) {
                  setDoc((prev) => ({ ...prev, doc_date: "" }));
                  return;
                }
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const formattedDocDate = `${year}-${month}-${day}T00:00:00`;

                // Calculate due date (docDate + 30 days)
                const dueDate = new Date(date);
                const enteredDays = paymentDays(doc);
                dueDate.setDate(dueDate.getDate() + (enteredDays === '' ? clientPaymentDays : enteredDays));
                const dueYear = dueDate.getFullYear();
                const dueMonth = String(dueDate.getMonth() + 1).padStart(
                  2,
                  "0",
                );
                const dueDay = String(dueDate.getDate()).padStart(2, "0");
                const formattedDueDate = `${dueYear}-${dueMonth}-${dueDay}T00:00:00`;

                setDoc((prev) => ({
                  ...prev,
                  doc_date: formattedDocDate,
                  bxdatascadenta: formattedDueDate,
                }));
              }}
              dateFormat="dd/MM/yyyy"
              className="form-control"
              disabled={locked}
              placeholderText="Selectati data"
            />
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group>
            <Form.Label>Scadenta</Form.Label>
            <DatePicker
              selected={
                doc.bxdatascadenta ? new Date(doc.bxdatascadenta) : null
              }
              onChange={(date) => {
                if (!date) {
                  setField("bxdatascadenta", "");
                  return;
                }
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                setField("bxdatascadenta", `${year}-${month}-${day}T00:00:00`);
              }}
              dateFormat="dd/MM/yyyy"
              className="form-control"
              disabled={locked}
              placeholderText="Selectati scadenta"
            />
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group>
            <Form.Label>Valuta</Form.Label>
            <Form.Control
              readOnly
              disabled
              value={(() => {
                const valutaId = clientAcc?.bxvaluta_id ?? doc.bxvaluta_id ?? 1;
                return valutaId === 2 ? "EURO" : valutaId === 3 ? "USD" : "RON";
              })()}
              className="fw-semibold"
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group controlId="contact_id">
            <Form.Label>Client</Form.Label>

            <div className="d-flex flex-wrap gap-2 align-items-start">
              <div
                role="button"
                onClick={() => !locked && setClientPicker(true)}
                style={{ flex: "0 0 100%", minWidth: 0 }}
                className={`header-clickable w-100 ${locked ? "disabled" : ""}`}
                title={locked ? "" : "Click pentru a alege contactul"}
              >
                <Form.Control
                  readOnly
                  disabled={locked}
                  value={doc.contact_label || ""}
                  placeholder="Click pentru a alege contactul..."
                />
              </div>

              <Button
                variant="secondary"
                className="ms-2"
                onClick={() => window.open(`/iesire/${doc.id}/print`, "_blank")}
              >
                Print
              </Button>

              {/* <Button
                variant="secondary"
                onClick={() => window.open(`/iesire/${doc.id}/pdf`, "_blank")}
           
              >
                PDF
              </Button> */}
              <Button type="button" onClick={() => openPdf(doc.id)}>
                PDF
              </Button>
              <CashRegisterButton invoiceId={doc.id} disabled={busy || String(doc.tip_document || 'Factura').toLowerCase()!=='factura'} />

              {/* <Button
                variant="secondary"
                onClick={() =>
                  window.open(
                    `/rapoarte/jurnal-tva/print?from=${from}&to=${to}&tip=iesire`,
                    "_blank",
                  )
                }
              >
                Print Jurnal TVA
              </Button> */}

              {/* <Button
                variant="outline-primary"
                disabled={locked}
                onClick={() => setClientPicker(true)}
              >
                Alege
              </Button> */}

              {/* {doc.contact_id && (
                <Button
                  variant="outline-secondary"
                  disabled={locked}
                  onClick={() => {
                    setField("contact_id", null);
                    setField("contact_label", "");
                  }}
                >
                  Sterge
                </Button>
              )} */}
            </div>

            <ClientPickerModal
              show={clientPicker}
              onHide={() => setClientPicker(false)}
              minChars={0}
              onPick={(opt) => {
                setField("contact_id", opt.id);
                setField("contact_label", opt.label);
                setErr((prev) => (prev === "Alege clientul." ? "" : prev));
              }}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        {(!doc.tip_document || doc.tip_document === "Factura") && (
          <Col md={2}>
          <Form.Group controlId="tip_factura_iesire_id">
            <Form.Label>Clasificare fiscală</Form.Label>

            <div className="d-flex gap-2 align-items-start">
              <div
                role="button"
                onClick={() => !locked && openTipFacturaPicker(true)}
                className={`header-clickable w-100 ${locked ? "disabled" : ""}`}
                title={locked ? "" : "Click pentru a alege clasificarea fiscală"}
              >
                <Form.Control
                  readOnly
                  disabled={locked}
                  value={Number(doc.tip_factura_iesire_id) === 4 ? "Notă de creditare / reducere" : (doc.tip_factura_iesire_label || "")}
                  placeholder="Click pentru a alege clasificarea fiscală..."
                />
              </div>

              {/* <Button
                variant="outline-primary"
                disabled={locked}
                onClick={() => settipFacturaPicker(true)}
              >
                Alege
              </Button>

              {doc.tip_factura_iesire_id && (
                <Button
                  variant="outline-secondary"
                  disabled={locked}
                  onClick={() => {
                    setField("tip_factura_iesire_id", null);
                    setField("tip_factura_iesire_label", "");
                  }}
                >
                  Sterge
                </Button>
              )} */}
            </div>

            <TipFacturaPickerModal
              show={tipFacturaPicker}
              onHide={() => settipFacturaPicker(false)}
              minChars={0}
              onPick={handleTipFacturaChange}
              // onPick={(opt) => {
              //   setField("tip_factura_iesire_id", opt.id);
              //   setField("tip_factura_iesire_label", opt.label); // tip_factura
              // }}
            />
          </Form.Group>
        </Col>
          )}

        {isForeignCurrency && (
          <Col md={2}>
            <Form.Group controlId="bxvaluta_curs">
              <Form.Label>Curs Valuta</Form.Label>
              <Form.Control
                type="number"
                step="0.0001"
                disabled={locked}
                value={doc.bxvaluta_curs || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setDoc((prev) => ({
                    ...prev,
                    bxvaluta_curs: val,
                    bxcurs_invoice: val,
                  }));
                }}
              />
            </Form.Group>
          </Col>
        )}

        <Col md={2}>
          <Form.Group>
            <Form.Label>Comanda client</Form.Label>
            <Form.Control
              disabled={locked}
              value={doc.comanda_client || ""}
              onChange={(e) => setField("comanda_client", e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group>
            <Form.Label>Proiect client</Form.Label>
            <Form.Control
              disabled={locked}
              value={doc.proiect_client || ""}
              onChange={(e) => setField("proiect_client", e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={isForeignCurrency ? 12 : 6}>
          <Form.Group>
            <Form.Label>Observatii</Form.Label>
            <Form.Control
              disabled={locked}
              value={doc.observatii || ""}
              onChange={(e) => setField("observatii", e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* LINES */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Linii</h5>
        <div className="d-flex gap-2 flex-wrap">
          {doc.tip_document === "Factura" && <InvoiceDiscount doc={doc}
            disabled={locked || busy || statementInvoice} makeUid={makeUid}
            onApply={linii => setDoc(prev => ({ ...prev, linii }))} />}
          <Button
            type="button"
            variant="outline-info"
            onClick={() => {
              if (!doc.contact_id) {
                toast.warning("Selecteaza mai intai un client.");
                return;
              }
              setShowAvizPicker(true);
            }}
            disabled={(locked || statementInvoice)}
          >
            Preluare din aviz
          </Button>
          {doc.id && String(doc.tip_document||'Factura').toLowerCase()==='factura' && Number(doc.bxtotalwtva)>0 && <InvoiceStornoButton invoiceId={doc.id} disabled={busy}/>}
          {!doc.id && <Button
            type="button"
            variant="outline-warning"
            onClick={() => {
              if (!doc.contact_id) {
                toast.warning("Selecteaza mai intai un client.");
                return;
              }
              setShowStornoPicker(true);
            }}
            disabled={(locked || statementInvoice)}
          >
            Storno din istoric
          </Button>}
          <Button variant="outline-primary" onClick={addLine} disabled={(locked || statementInvoice)}>
            + Linie
          </Button>
        </div>
      </div>
      <div className="text-muted mb-2" style={{ fontSize: 12 }}>
        Tip: da <b>click</b> pe celulele <b>Gestiune</b> si <b>Part</b> pentru a
        alege din lista.
      </div>
      <Table bordered size="sm" className="w-100 table-fixed">
        <thead>
          <tr>
            <th style={{ width: 140 }}>Gestiune</th>
            <th style={{ width: 420 }}>Part</th>

            <th style={{ width: 420 }}>Descriere</th>
            <th style={{ width: 80 }} className="text-end">
              Stoc
            </th>
            <th style={{ width: 180 }} className="text-end">
              Qty
            </th>

            <th style={{ width: 180 }} className="text-end">
              Price
            </th>
            {isPlatitorTva && (
              <th style={{ width: 110 }} className="text-end">
                TVA %
              </th>
            )}
            <th style={{ width: 180 }} className="text-end">
              Suma fara tva
            </th>
            {isPlatitorTva && (
              <th style={{ width: 140 }} className="text-end">
                Suma tva
              </th>
            )}
            <th style={{ width: cuSerii ? 170 : 100, textAlign: "center" }}>Actiuni</th>
          </tr>
        </thead>

        <tbody>
          {(doc.linii || []).map((ln, i) => {
            const isService = ln.bxgestiune_id == null;
            const isStock = Number(ln.tip_grupa_produse ?? 2) === 2;

            const availTxt = (ln.stock_total == null || !isStock)
              ? "#"
              : Number(ln.stock_total).toFixed(3);

            return (
              <tr key={i}>
                {/* GESTIUNE */}
                <td
                  role="button"
                  onClick={() => !(locked || statementInvoice || !!ln.environmental_fee) && openGestiunePicker(i)}
                  style={{ cursor: (locked || statementInvoice || !!ln.environmental_fee) ? "default" : "pointer" }}
                  title={(locked || statementInvoice || !!ln.environmental_fee) ? "" : "Click pentru gestiune"}
                  // className={(locked || statementInvoice || !!ln.environmental_fee) ? "" : "bg-light"}
                  className={`td-clickable ${(locked || statementInvoice || !!ln.environmental_fee) ? "disabled" : ""}`}
                >
                  {ln.bxgestiune_id == null ? (
                    <span className="text-muted  small">Serviciu</span>
                  ) : (
                    ln.bxgestiune_label || `#${ln.bxgestiune_id}`
                  )}
                </td>

                {/* PART */}
                <td
                  role="button"
                  onClick={() => !(locked || statementInvoice || !!ln.environmental_fee) && openPartPicker(i)}
                  className={`td-clickable ${(locked || statementInvoice || !!ln.environmental_fee) ? "disabled" : ""}`}
                  style={{ cursor: (locked || statementInvoice || !!ln.environmental_fee) ? "default" : "pointer" }}
                  title={(locked || statementInvoice || !!ln.environmental_fee) ? "" : "Click pentru a alege articolul"}
                  // className={(locked || statementInvoice || !!ln.environmental_fee) ? "" : "bg-light"}
                >
                  {ln.bxpart_id ? (
                    <span className="small">
                      {ln.bxpart_label || `#${ln.bxpart_id}`}
                    </span>
                  ) : (
                    <span className="text-muted small">
                      Click pentru alegere articol…
                    </span>
                  )}
                </td>

                {/* DESCRIERE */}
                <td>
                  <Form.Control
                    size="sm"
                    disabled={(locked || statementInvoice || !!ln.environmental_fee)}
                    value={ln.descriere || ""}
                    onChange={(e) =>
                      updateLine(i, { descriere: e.target.value })
                    }
                    placeholder="scrie ce doresti..."
                  />
                  
                </td>
                <td className="text-end">
                  {ln.loadingAvail ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    availTxt
                  )}
                </td>

                {/* <Form.Control
                  size="sm"
                  type="text" // 👈 important
                  inputMode="decimal"
                  disabled={(locked || statementInvoice || !!ln.environmental_fee)}
                  value={/[.,]\d{5}/.test(String(ln.qty ?? "")) ? format4(ln.qty) : (ln.qty ?? "")}
                  onChange={(e) => onQtyChange(i, e.target.value)}
                  onBlur={() => {
                    const v = Number(String(ln.qty).replace(",", "."));
                    updateLine(i, {
                      qty: Number.isFinite(v) ? v.toFixed(4) : "",
                    });
                  }}
                  className="no-spin text-end"
                /> */}
                {/* <Form.Control
                  size="sm"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  disabled={(locked || statementInvoice || !!ln.environmental_fee)}
                  value={format4(ln.qty)}
                  onChange={(e) => onQtyChange(i, e.target.value)}
                  onBlur={() => {
                    const v = Number(String(ln.qty).replace(",", "."));
                    updateLine(i, {
                      qty: Number.isFinite(v) ? v.toFixed(4) : "",
                    });
                  }}
                  className="no-spin text-end"
                /> */}

                <td className="text-end">
                  {(locked || statementInvoice || !!ln.environmental_fee) ? (
                    format4(ln.qty)
                  ) : (
                    <Form.Control
                      size="sm"
                      type="text"
                      inputMode="decimal"
                      value={/[.,]\d{5}/.test(String(ln.qty ?? "")) ? format4(ln.qty) : (ln.qty ?? "")}
                      onChange={(e) => updateLine(i, { qty: e.target.value })}
                      onBlur={() => {
                        const v = Number(String(ln.qty).replace(",", "."));
                        updateLine(i, {
                          qty: Number.isFinite(v) ? v.toFixed(4) : "",
                        });
                      }}
                      className="no-spin text-end"
                    />
                  )}
                </td>

                {/* <td>
                  <Form.Control
                    size="sm"
                    type="number" // 👈 nu number, ca sa nu te incurce browserul
                    inputMode="decimal"
                    disabled={(locked || statementInvoice || !!ln.environmental_fee)}
                    value={ln.price ?? ""} // 👈 pastreaza raw text cat tasteaza
                    onChange={(e) => updateLine(i, { price: e.target.value })}
                    // onBlur={() => {
                    //   const v = Number(String(ln.price).replace(",", "."));
                    //   updateLine(i, {
                    //     price: Number.isFinite(v) ? v.toFixed(4) : "",
                    //   });
                    // }}
                    className="no-spin text-end"
                  />
                </td> */}
                <td className="text-end">
                  {(locked || statementInvoice || !!ln.environmental_fee) ? (
                    format4(ln.price)
                  ) : (
                    <Form.Control
                      size="sm"
                      type="text"
                      inputMode="decimal"
                      value={ln.price ?? ""}
                      onChange={(e) => updateLine(i, { price: e.target.value })}
                      onBlur={() => {
                        const v = Number(String(ln.price).replace(",", "."));
                        updateLine(i, {
                          price: Number.isFinite(v) ? v.toFixed(4) : "",
                        });
                      }}
                      className="no-spin text-end"
                    />
                  )}
                </td>
                {isPlatitorTva && (
                  <td
                    role={(locked || statementInvoice || !!ln.environmental_fee) || !isPlatitorTva ? undefined : "button"}
                    onClick={() => !(locked || statementInvoice || !!ln.environmental_fee) && isPlatitorTva && openVatPicker(i)}
                    className={`text-end small ${!(locked || statementInvoice || !!ln.environmental_fee) && isPlatitorTva ? "td-clickable td-picker" : "text-muted"}`}
                    title={
                      (locked || statementInvoice || !!ln.environmental_fee) || !isPlatitorTva ? "" : "Click pentru a alege TVA"
                    }
                  >
                    {ln.vat_label ? (
                      ln.vat_label.replace("%", "")
                    ) : (ln.vat_rate !== undefined && ln.vat_rate !== null && ln.vat_rate !== "") ? (
                      <span>{Number(ln.vat_rate).toFixed(0)}%</span>
                    ) : (
                      <span className="text-muted">…</span>
                    )}
                  </td>
                )}
                <td className={`text-end small ${(locked || statementInvoice || !!ln.environmental_fee) ? "text-muted" : ""}`}>
                  {calcFaraTVA(ln).toFixed(2)}
                </td>

                {isPlatitorTva && (
                  <td
                    className={`text-end small ${(locked || statementInvoice || !!ln.environmental_fee) ? "text-muted" : ""}`}
                  >
                    {calcTVA(ln).toFixed(2)}
                  </td>
                )}
                <td>
                  <div className="d-flex flex-nowrap justify-content-center align-items-center gap-2">
                    <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={(locked || statementInvoice || !!ln.environmental_fee)}
                    onClick={() => delLine(i)}
                  >
                    Sterge
                  </Button>
                    {cuSerii && ln.bxpart_id && (
                    <Button 
                      size="sm" 
                      variant={ln.serii?.length ? "success" : "secondary"} 
                      className="m-0" 
                      style={{ whiteSpace: "nowrap" }} onClick={() => setSeriiPicker({show: true, row: i})}
                      disabled={locked || statementInvoice || !!ln.environmental_fee}
                    >
                      Serii ({ln.serii?.length || 0})
                    </Button>
                  )}
                  </div>
                </td>
              </tr>
            );
          })}

          {(doc.linii || []).length === 0 && (
            <tr>
              <td
                colSpan={isPlatitorTva ? 7 : 5}
                className="text-muted text-center py-3"
              >
                Nicio linie
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {!!doc.deviz_id && <Alert variant="info">Liniile provin din deviz și nu pot fi modificate sau șterse din factură.</Alert>}
      <InvoicePrintDetails doc={doc} setField={setField} disabled={locked || busy} />
      <InvoiceTotals lines={doc.linii} vatEnabled={isPlatitorTva}
        currency={Number(clientAcc?.bxvaluta_id ?? doc.bxvaluta_id ?? 1) === 2 ? "EUR"
          : Number(clientAcc?.bxvaluta_id ?? doc.bxvaluta_id ?? 1) === 3 ? "USD" : "RON"} />

      {!locked && <InvoiceCostAllocation ref={allocationRef} doc={doc} disabled={busy}
        onChange={(cost_allocation, meta) => setDoc(d => ({ ...d, cost_allocation,
          _allocationBaseline: d._allocationBaseline ?? (meta?.automatic ? cost_allocation : undefined) }))} />}
      {showActions && <div className="d-flex justify-content-end gap-2 mt-3">
        <Button type="submit" variant="primary" disabled={locked || busy}>
          Salveaza
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={onCancel || (() => navigate(-1))}>
          {doc.id ? "Înapoi" : "Renunță"}
        </Button>
      </div>}

      <GestiunePickerModal
        show={gestPicker.show}
        onHide={() => setGestPicker({ show: false, row: null })}
        onPick={(opt) => {
          const row = gestPicker.row;
          if (row == null) return;

          // updateLine(row, {
          //   bxgestiune_id: opt?.id ?? null,
          //   bxgestiune_label: opt?.label ?? "",
          //   stock_total: 0,
          //   stock_lots: [],
          //   alloc: [],
          //   stock_err: "",
          // });
          const ln = doc.linii?.[row];
          const partId = ln?.bxpart_id ?? null;

          const newGestId = opt?.id ?? null;
          updateLine(row, {
            bxgestiune_id: newGestId,
            bxgestiune_label: opt?.label ?? "",
            stock_total: 0,
            loadingAvail: false,
            stock_lots: [],
            alloc: [],
            stock_err: "",
          });

          if (newGestId && partId) {
            const uid = ln?.uid;
            const key = makeAvailKey(newGestId, doc.doc_date, partId);
            lastAvailKeyRef.current.set(uid, key);
            fetchAvailForLine(uid, newGestId, partId);
          }
        }}
      />

      <VatPickerModal
        show={vatPicker.show}
        onHide={() => setVatPicker({ show: false, row: null })}
        onPick={(opt) => {
          const row = vatPicker.row;
          if (row == null) return;

          updateLine(row, {
            vat_id: opt.id,
            vat_rate: opt.procent,
            vat_label: opt.label,
          });
        }}
      />

      <StocCurentPickerModal
        show={partPicker.show && rowGestiuneId != null}
        onHide={() => setPartPicker({ show: false, row: null })}
        gestiuneId={rowGestiuneId}
        onPick={(opt) => {
          const row = partPicker.row;
          if (row == null) return;

          const partId = opt?.id ?? null;
          const partRaw = opt?.raw;

          const tip = Number(
            partRaw?.tip_grupa_produse ??
              partRaw?.tip_grupa ??
              partRaw?.tip ??
              2,
          );
          const isStock = tip === 2;
          const gp = partRaw?.grupa_produse;
          const vatId = gp?.cota_tva_id ?? partRaw?.cota_tva_id ?? null;
          const vatRate = gp?.bxvat_procent ?? partRaw?.bxvat_procent ?? 0;
          const vatLabel = gp?.bxvat_cod ?? partRaw?.bxvat_cod ?? "";

          updateLine(row, {
            bxpart_id: partId,
            bxpart_label: opt?.label ?? "",
            tip_grupa_produse: tip,
            vat_id: vatId,
            vat_rate: vatRate,
            vat_label: vatLabel,
            stock_lots: [],
            alloc: [],
            stock_err: "",
            stock_total: isStock ? (partRaw?.cantitate ?? 0) : null,
            bxstocin_id: null,
            ...(isStock ? {} : { bxgestiune_id: null }),
          });

          // auto descriere
          const name =
            partRaw?.denumire ??
            partRaw?.bxname ??
            partRaw?.name ??
            partRaw?.descriere ??
            null;
          if (name && !(doc.linii?.[row]?.descriere || "").trim()) {
            updateLine(row, { descriere: name });
          }

          if (clientCatalogId && partId) {
            authGetJson(
              `/pret-catalog/lookup?catalog_id=${clientCatalogId}&bxpart_id=${partId}`,
            )
              .then((catalogPriceData) => {
                if (catalogPriceData && catalogPriceData.pret != null) {
                  updateLine(row, {
                    price: Number(catalogPriceData.pret).toFixed(4),
                  });
                } else {
                  const p =
                    partRaw?.pret_vanzare ??
                    partRaw?.price_sale ??
                    partRaw?.price ??
                    partRaw?.pret ??
                    null;
                  if (p != null)
                    updateLine(row, { price: Number(p).toFixed(4) });
                }
              })
              .catch(() => {
                const p =
                  partRaw?.pret_vanzare ??
                  partRaw?.price_sale ??
                  partRaw?.price ??
                  partRaw?.pret ??
                  null;
                if (p != null) updateLine(row, { price: Number(p).toFixed(4) });
              });
          } else {
            const p =
              partRaw?.pret_vanzare ??
              partRaw?.price_sale ??
              partRaw?.price ??
              partRaw?.pret ??
              null;
            if (p != null) updateLine(row, { price: Number(p).toFixed(4) });
          }
        }}
      />

      <PartPickerModal
        show={partPicker.show && rowGestiuneId == null}
        onHide={() => setPartPicker({ show: false, row: null })}
        minChars={0} // pune 2 daca vrei doar dupa 2 caractere
        filter={{ tip_grupa_produse: grupa_produse }}
        onPick={(opt) => {
          const row = partPicker.row;
          if (row == null) return;

          const partId = opt?.id ?? null;
          const partRaw = opt?.raw;

          const tip = Number(
            partRaw?.tip_grupa_produse ??
              partRaw?.tip_grupa ??
              partRaw?.tip ??
              2,
          );
          const isStock = tip === 2;
          const gp = partRaw?.grupa_produse;
          const vatId = gp?.cota_tva_id ?? partRaw?.cota_tva_id ?? null;
          const vatRate = gp?.bxvat_procent ?? partRaw?.bxvat_procent ?? 0;
          const vatLabel = gp?.bxvat_cod ?? partRaw?.bxvat_cod ?? "";

          updateLine(row, {
            bxpart_id: partId,
            bxpart_label: opt?.label ?? "",
            tip_grupa_produse: tip,
            vat_id: vatId,
            vat_rate: vatRate,
            vat_label: vatLabel,
            stock_lots: [],
            alloc: [],
            stock_err: "",
            stock_total: isStock ? 0 : null,
            ...(isStock ? {} : { bxgestiune_id: null }),
          });

          const ln = doc.linii?.[row];
          const gestId = ln?.bxgestiune_id ?? null;
          const uid = ln?.uid;

          if (isStock && gestId && partId) {
            const key = makeAvailKey(gestId, doc.doc_date, partId);
            lastAvailKeyRef.current.set(uid, key);
            fetchAvailForLine(uid, gestId, partId);
          }

          // optional: auto descriere / price
          const name =
            partRaw?.denumire ??
            partRaw?.bxname ??
            partRaw?.name ??
            partRaw?.descriere ??
            null;
          if (name && !(doc.linii?.[row]?.descriere || "").trim()) {
            updateLine(row, { descriere: name });
          }

          if (clientCatalogId && partId) {
            authGetJson(
              `/pret-catalog/lookup?catalog_id=${clientCatalogId}&bxpart_id=${partId}`,
            )
              .then((catalogPriceData) => {
                if (catalogPriceData && catalogPriceData.pret != null) {
                  updateLine(row, {
                    price: Number(catalogPriceData.pret).toFixed(4),
                  });
                } else {
                  const p =
                    partRaw?.pret_vanzare ??
                    partRaw?.price_sale ??
                    partRaw?.price ??
                    partRaw?.pret ??
                    null;
                  if (p != null)
                    updateLine(row, { price: Number(p).toFixed(4) });
                }
              })
              .catch(() => {
                const p =
                  partRaw?.pret_vanzare ??
                  partRaw?.price_sale ??
                  partRaw?.price ??
                  partRaw?.pret ??
                  null;
                if (p != null) updateLine(row, { price: Number(p).toFixed(4) });
              });
          } else {
            const p =
              partRaw?.pret_vanzare ??
              partRaw?.price_sale ??
              partRaw?.price ??
              partRaw?.pret ??
              null;
            if (p != null) updateLine(row, { price: Number(p).toFixed(4) });
          }

          // aici poti chema fetchLotsForPart daca vrei (doar pe click + pick)
        }}
      />
      <AvizePickerModal
        show={showAvizPicker}
        onHide={() => setShowAvizPicker(false)}
        contactId={doc.contact_id}
        onPick={handlePickAviz}
      />
      <SeriiVanzareModal
        readOnly={locked}
        show={seriiPicker.show}
        onHide={() => setSeriiPicker({ show: false, row: null })}
        line={seriiPicker.row !== null ? doc.linii[seriiPicker.row] : null}
        onSave={(serii) => {
          if (seriiPicker.row !== null) {
            updateLine(seriiPicker.row, { serii });
          }
        }}
      />
      <StornoPickerModal
        show={showStornoPicker}
        onHide={() => setShowStornoPicker(false)}
        contactId={doc.contact_id}
        onPick={handlePickStorno}
      />
    </Form>
  );
}

